import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ViewEncapsulation, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslocoDirective, TranslocoPipe, provideTranslocoScope } from '@jsverse/transloco';
import { AccountCreationRequest, AuthApiService } from '@rma/accounts/public/orc-authentication';
import { RmaBackButtonDirective } from '@rma/generic/ui/back-button';
import { CalloutComponent } from '@rma/generic/ui/callout';
import { provideRmaErrorStateMatcher } from '@rma/generic/ui/forms/combined';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { LoadingIndicatorDirective } from '@rma/generic/ui/loading-indicator';
import { SplitMarketingLayoutComponent } from '@rma/generic/ui/split-marketing-layout';
import { StaticImageUrlPipe } from '@rma/generic/util/environment';
import { FeatureFlagService, FeatureFlags, IfFeatureFlagDirective, TempFeatureFlags } from '@rma/generic/util/feature-flags';
import { SubmissionFormFactoryService } from '@rma/generic/util/form-submission';
import { exhaustMap, map } from 'rxjs';

interface FormOption {
  label: string;
  value: string;
}

interface FormOptions {
  countries: FormOption[];
  industries: FormOption[];
  roles: FormOption[];
}

const form = (fb: NonNullableFormBuilder) => {
  return fb.group({
    country: fb.control<string | undefined>(undefined),
    industry: fb.control<string | undefined>(undefined),
    role: fb.control<string | undefined>(undefined),
    firstName: fb.control<string>('', Validators.required),
    lastName: fb.control<string>('', Validators.required),
    email: fb.control<string>('', [Validators.required, Validators.email]),
    phone: fb.control<string>('', Validators.required),
    agencyName: fb.control<string | undefined>(undefined, Validators.required),
    narNumber: fb.control<string | undefined>(undefined),
  });
};

const defaultFormOptions: FormOptions = {
  countries: [],
  industries: [],
  roles: [],
};

@Component({
  selector: 'rma-create-profile',
  imports: [
    AsyncPipe,
    CalloutComponent,
    IfFeatureFlagDirective,
    LoadingIndicatorDirective,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    RmaBackButtonDirective,
    RmaIconComponent,
    RouterModule,
    SplitMarketingLayoutComponent,
    StaticImageUrlPipe,
    TranslocoDirective,
    TranslocoPipe,
  ],
  providers: [
    provideTranslocoScope('searchprofile'),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    provideRmaErrorStateMatcher(),
  ],
  templateUrl: './create-profile.component.html',
  styleUrl: './create-profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CreateProfileComponent {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly authApiService = inject(AuthApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly submissionFormFactoryService = inject(SubmissionFormFactoryService);

  private readonly submissionSuccessSignal = signal<boolean | undefined>(undefined);

  public readonly mandatoryLicenseFlag = FeatureFlags.HasMandatoryLicenceField;
  public readonly profileForm = this.createForm();
  public readonly profileCreationSuccess = this.submissionSuccessSignal.asReadonly();

  public readonly formOptions$ = this.featureFlagService.getFeature$<FormOptions>(
    TempFeatureFlags.CreateProfileTrackCountryIndustryRole,
    defaultFormOptions,
  );
  public readonly showCallout$ = this.profileForm.form.valueChanges.pipe(
    map(({ country, industry, role }) => this.mapToShowCallout(country, industry, role)),
  );

  public constructor() {
    this.formOptions$.pipe(takeUntilDestroyed()).subscribe((formOptions) => {
      this.profileForm.controls.country.setValue(formOptions.countries?.[0]?.value);
      this.profileForm.controls.industry.setValue(formOptions.industries?.[0]?.value);
      this.profileForm.controls.role.setValue(formOptions.roles?.[0]?.value);
    });

    if (this.activatedRoute.snapshot.queryParamMap.has('email')) {
      this.profileForm.controls.email.setValue(this.activatedRoute.snapshot.queryParamMap.get('email') ?? '');
    }
  }

  private createForm() {
    const submissionForm = this.submissionFormFactoryService.createSubmissionForm<AccountCreationRequest, void>(
      form(this.fb),
      exhaustMap((request) => this.authApiService.requestAccountCreation(request)),
    );

    submissionForm.onSuccess$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.submissionSuccessSignal.set(true);
    });

    submissionForm.onError$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((error) => {
      console.error(error);
      this.submissionSuccessSignal.set(false);
    });

    return submissionForm;
  }

  private mapToShowCallout(country: string | undefined, industry: string | undefined, role: string | undefined): boolean {
    return !!(country === 'Canada' || industry === 'Finance' || industry === 'Insurance' || role === 'Rentals');
  }
}
